// src/utils/body-lock.js
var bodyLock = class {
  /**
   * lock the body to prevent scrolling.
   */
  lock() {
    const bodyElement = document.querySelector("body");
    bodyElement.setAttribute("data-body-lock", "true");
  }
  /**
   * unlock the scrolling of the body.
   */
  unlock() {
    const bodyElement = document.querySelector("body");
    bodyElement.setAttribute("data-body-lock", "false");
  }
};
var body_lock_default = new bodyLock();

// src/extenders/product-media.js
var ProductMediaGallery = class extends HTMLElement {
  /**
   * Initialize the gallery and set up required elements and event listeners.
   */
  constructor() {
    super();
    this.mediaItems = this.querySelectorAll("[data-media-id]");
    this.currentIndex = 0;
    this.isModalOpen = false;
    this.translations = {
      close: this.getAttribute("data-text-close"),
      previous: this.getAttribute("data-text-previous"),
      next: this.getAttribute("data-text-next"),
      imageXofY: this.getAttribute("data-text-image-x-of-y")
    };
    this._initModal();
    this._bindEvents();
    this._setupImageListKeyboardNav();
  }
  /**
   * Initialize the modal structure and create necessary DOM elements.
   * @private
   */
  _initModal() {
    if (!document.getElementById("media-modal")) {
      const modal = document.createElement("div");
      modal.id = "media-modal";
      modal.className = "hidden fixed inset-0 z-50 justify-center items-center bg-white";
      modal.setAttribute("aria-hidden", "true");
      modal.innerHTML = `
        <div class="flex relative justify-center items-center w-full h-full">
          <div class="sr-only" role="status" aria-live="polite"></div>
          
          <div class="flex absolute inset-0 flex-col">
            <button 
              class="flex absolute top-4 right-4 z-10 justify-center items-center p-2 w-10 h-10 rounded-full duration-300 text-linnen bg-jet hover:opacity-75"
              aria-label="${this.translations.close}"
              data-modal-close
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <!-- Main content area with navigation -->
            <div class="flex relative flex-1 justify-center items-center">
              <button 
                class="flex absolute left-4 top-1/2 z-10 justify-center items-center p-2 w-10 h-10 rounded-full duration-300 -translate-y-1/2 text-linnen bg-jet hover:opacity-75" 
                aria-label="${this.translations.previous}"
                data-modal-prev
              >
                <svg
                  width='16'
                  height='13'
                  viewBox='0 0 16 13'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  class='rotate-180'
                >
                  <path d="M15 6.5c-2.896.563-6 4.05-6 6m6-6c-2.896-.562-6-4.05-6-6m6 6H0" stroke="currentColor" stroke-width="1.5"/>
                </svg>
              </button>

              <div class="p-4 w-full h-full">
                <div class="flex relative justify-center items-center w-full h-full">
                  <img 
                    class="object-contain max-w-full max-h-full rounded-xl opacity-0 transition-opacity duration-150 h-[90vh]" 
                    src="" 
                    alt=""
                    data-modal-image
                  />
                  <div class="flex absolute inset-0 justify-center items-center" data-modal-loading>
                    <div class="w-8 h-8 rounded-full border-4 animate-spin border-white/20 border-t-white"></div>
                  </div>
                </div>
              </div>

              <button 
                class="flex absolute right-4 top-1/2 z-10 justify-center items-center p-2 w-10 h-10 rounded-full duration-300 -translate-y-1/2 text-linnen hover:opacity-75 bg-jet" 
                aria-label="${this.translations.next}"
                data-modal-next
              >
                <svg
                  width='16'
                  height='13'
                  viewBox='0 0 16 13'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path d="M15 6.5c-2.896.563-6 4.05-6 6m6-6c-2.896-.562-6-4.05-6-6m6 6H0" stroke="currentColor" stroke-width="1.5"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
    }
    this.modal = document.getElementById("media-modal");
    this.modalImage = this.modal.querySelector("[data-modal-image]");
    this.modalLoading = this.modal.querySelector("[data-modal-loading]");
    this.closeButton = this.modal.querySelector("[data-modal-close]");
    this.prevButton = this.modal.querySelector("[data-modal-prev]");
    this.nextButton = this.modal.querySelector("[data-modal-next]");
    this.statusElement = this.modal.querySelector('[role="status"]');
  }
  /**
   * Set up keyboard navigation for the image list.
   * @private
   */
  _setupImageListKeyboardNav() {
    this.mediaItems.forEach((item, index) => {
      item.setAttribute("tabindex", "0");
      item.setAttribute("role", "button");
      item.setAttribute("aria-label", this._getImageLabel(index));
      item.addEventListener("keydown", (e) => {
        switch (e.key) {
          case "Enter":
          case " ":
            e.preventDefault();
            this._openModal(index);
            break;
          case "ArrowRight":
          case "ArrowDown":
            e.preventDefault();
            const nextItem = this.mediaItems[index + 1] || this.mediaItems[0];
            nextItem.focus();
            break;
          case "ArrowLeft":
          case "ArrowUp":
            e.preventDefault();
            const prevItem = this.mediaItems[index - 1] || this.mediaItems[this.mediaItems.length - 1];
            prevItem.focus();
            break;
          case "Home":
            e.preventDefault();
            this.mediaItems[0].focus();
            break;
          case "End":
            e.preventDefault();
            this.mediaItems[this.mediaItems.length - 1].focus();
            break;
        }
      });
    });
  }
  /**
   * Get the formatted label for an image at a specific index.
   * @param {number} index - The index of the image
   * @returns {string} Formatted label string
   * @private
   */
  _getImageLabel(index) {
    return this.translations.imageXofY.replace("{{ current }}", index + 1).replace("{{ total }}", this.mediaItems.length);
  }
  /**
   * Re-query the DOM for media items and re-bind click and keyboard events.
   * Call this after the gallery thumbnail list has been updated programmatically
   * (e.g. after a configurator pattern change syncs new images into the gallery).
   */
  refreshItems() {
    this.mediaItems = this.querySelectorAll("[data-media-id]");
    this.currentIndex = 0;
    this._bindMediaItemEvents();
    this._setupImageListKeyboardNav();
  }
  /**
   * Bind click listeners to all current media items.
   * Extracted so it can be called independently on refresh.
   * @private
   */
  _bindMediaItemEvents() {
    this.mediaItems.forEach((item, index) => {
      item.addEventListener("click", () => this._openModal(index));
    });
  }
  /**
   * Bind all necessary event listeners.
   * @private
   */
  _bindEvents() {
    this._bindMediaItemEvents();
    this.closeButton.addEventListener("click", () => this._closeModal());
    this.prevButton.addEventListener("click", () => this._navigate(-1));
    this.nextButton.addEventListener("click", () => this._navigate(1));
    document.addEventListener("keydown", (e) => {
      if (!this.isModalOpen)
        return;
      switch (e.key) {
        case "Escape":
          this._closeModal();
          break;
        case "ArrowLeft":
          this._navigate(-1);
          break;
        case "ArrowRight":
          this._navigate(1);
          break;
      }
    });
    this.modalImage.addEventListener("load", () => {
      this.modalLoading.classList.add("hidden");
      this.modalImage.classList.remove("opacity-0");
    });
  }
  /**
   * Open the modal at a specific index.
   * @param {number} index - The index of the image to show
   * @private
   */
  _openModal(index) {
    this.currentIndex = index;
    this.modalImage.classList.add("opacity-0");
    this.modalLoading.classList.remove("hidden");
    this.modal.classList.remove("hidden");
    this.modal.classList.add("flex");
    this.modal.setAttribute("aria-hidden", "false");
    this.isModalOpen = true;
    this._updateModalImage();
    this.closeButton.focus();
    body_lock_default.lock();
  }
  /**
   * Close the modal and clean up.
   * @private
   */
  _closeModal() {
    this.modalImage.classList.add("opacity-0");
    this.modalImage.src = "";
    this.modalImage.alt = "";
    this.modal.classList.add("hidden");
    this.modal.classList.remove("flex");
    this.modal.setAttribute("aria-hidden", "true");
    this.isModalOpen = false;
    this.mediaItems[this.currentIndex].focus();
    body_lock_default.unlock();
  }
  /**
   * Navigate to the next or previous image.
   * @param {number} direction - Direction to navigate (-1 for previous, 1 for next)
   * @private
   */
  _navigate(direction) {
    this.modalImage.classList.add("opacity-0");
    this.modalLoading.classList.remove("hidden");
    this.currentIndex = (this.currentIndex + direction + this.mediaItems.length) % this.mediaItems.length;
    this._updateModalImage();
  }
  /**
   * Update the modal image and associated status text.
   * @private
   */
  _updateModalImage() {
    const currentItem = this.mediaItems[this.currentIndex];
    const img = currentItem.querySelector("img");
    if (img) {
      const originalSrc = img.getAttribute("data-src") || img.src;
      this.modalImage.src = originalSrc;
      this.modalImage.alt = img.alt;
      if (this.statusElement) {
        this.statusElement.textContent = this._getImageLabel(this.currentIndex);
      }
    }
  }
};
if (!window.customElements.get("media-gallery")) {
  window.customElements.define("media-gallery", ProductMediaGallery);
}
